"use client";

import React, { useState } from "react";
import { Activity, ArrowRight, CircleCheck as CheckCircle2, FlaskConical, Layers, ShieldCheck, SlidersHorizontal as Sliders } from 'lucide-react';
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
const technologies = [{
  id: "pat",
  label: "Process Analytical Technology",
  title: "Real-Time In-Line Spectroscopic Monitoring",
  badge: "Process Control",
  description: "Continuous real-time quality verification for critical blend, reaction, and drying stages in pharmaceutical and chemical production lines.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/pat_spectroscopy_analyzer.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/pat_spectroscopy_analyzer.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/pat_spectroscopy_analyzer.png-wvc-sizes",
  imageAlt: "In-line spectroscopic process monitoring analyzer unit",
  specs: "NIR / Raman Spectroscopy | IP65 Rated | Electronic Records & Signatures",
  link: "/ProcessAnalyticalTechnologyPage",
  features: ["Real-time blend uniformity determination", "Immediate detection of drying endpoints", "Non-destructive multi-point fiber optic probes"],
  useCases: ["Solid dosage formulation blending", "Continuous direct compression monitoring", "Active pharmaceutical ingredient crystallization"]
}, {
  id: "powder",
  label: "Powder Testing",
  title: "Comprehensive Rheology & Flowability Profiling",
  badge: "Formulation Science",
  description: "Precision rheology and compressibility assessment tools designed to predict powder behaviour during manufacturing and hopper transfer.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/powder_rheology_system.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/powder_rheology_system.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/powder_rheology_system.png-wvc-sizes",
  imageAlt: "Automated powder testing and rheometer workstation",
  specs: "Shear Cell Testing | Automated Aeration | Density Metrics",
  link: "/PowderTestingPage",
  features: ["Dynamic flow energy measurement", "Wall friction and hopper half-angle calculations", "Controlled aeration and permeability analysis"],
  useCases: ["Pre-formulation screening of active ingredients", "Tablet press feeding optimization", "Dry powder inhaler (DPI) flow profiling"]
}, {
  id: "tablet",
  label: "Tablet Testing",
  title: "Automated Physical Parameter Characterization",
  badge: "Quality Control",
  description: "High-throughput physical measurement stations for hardness, weight, thickness, diameter, and dissolution integrity in regulated QA/QC labs.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/tablet_testing_station.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/tablet_testing_station.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/tablet_testing_station.png-wvc-sizes",
  imageAlt: "Automated tablet physical testing station",
  specs: "EP / USP Standard Compliant | Auto-feed | Integrated Calipers",
  link: "/TabletTestingPage",
  features: ["Multi-parameter testing in a single cycle", "Automated debris clearing and reject sorting", "Direct integration with laboratory LIMS databases"],
  useCases: ["In-process tablet compression testing", "Batch release quality assurance", "Coating thickness and integrity verification"]
}, {
  id: "raw-material",
  label: "Raw Material ID",
  title: "Handheld & Benchtop Molecular Identification",
  badge: "Warehouse & QC",
  description: "Rapid non-destructive spectral verification through transparent packaging for immediate container-by-container incoming goods release.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/raw_material_id_analyzer.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/raw_material_id_analyzer.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/raw_material_id_analyzer.png-wvc-sizes",
  imageAlt: "Handheld Raman raw material identification device",
  specs: "Handheld Raman / Spatially Offset | Pass/Fail Output | Audit Trail",
  link: "/RawMaterialIdentificationPage",
  features: ["Direct container verification in loading docks", "Through-barrier measurement without sample exposure", "Pre-loaded verified spectral library"],
  useCases: ["Incoming raw material dock inspection", "Chemical counterfeit and impurity detection", "Controlled substance quarantine verification"]
}];
const capabilities = [{
  icon: FlaskConical,
  title: "Research-Grade Accuracy",
  description: "Calibrated to strict European and US pharmacopeia tolerances with comprehensive validation data."
}, {
  icon: ShieldCheck,
  title: "Data Integrity & Compliance",
  description: "Full audit trails, electronic signatures, and secure data export formats for laboratory records."
}, {
  icon: Sliders,
  title: "Application-Led Support",
  description: "Hands-on method development, IQ/OQ execution, and local lifecycle service teams in Ireland and the UK."
}];
export default function TechnologyOverview() {
  const [activeTab, setActiveTab] = useState<string>(technologies[0].id);
  const prefersReducedMotion = useReducedMotion();
  const selectedTech = technologies.find(item => item.id === activeTab) || technologies[0];
  return <section id="technologyoverview" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="page-container">
        {/* Section Header */}
        <div className="max-w-3xl mb-12">
          <Badge variant="secondary" className="mb-3 px-3 py-1 font-medium">
            Analytical Instrumentation
          </Badge>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-foreground">
            Analytical Technology for Regulated Laboratories
          </h2>
          <p className="mt-3 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Application-focused instrumentation engineered to support drug development, solid dosage formulation, and process control across Ireland and the UK.
          </p>
        </div>

        {/* Technology Selector Tabs */}
        <div className="flex flex-wrap gap-2 mb-8 p-1.5 bg-muted/60 rounded-xl border border-border/80 w-full md:w-fit">
          {technologies.map((tech, i) => {
          const isActive = activeTab === tech.id;
          return <Button key={tech.id} data-index={i} variant={isActive ? "default" : "ghost"} size="sm" onClick={() => setActiveTab(tech.id)} className={`transition-colors rounded-lg font-medium text-xs sm:text-sm h-9 px-4 ${isActive ? "bg-primary text-primary-foreground shadow-xs" : "text-foreground hover:text-foreground hover:bg-background/80"}`}>
                {tech.label}
              </Button>;
        })}
        </div>

        {/* Dynamic Detail Card */}
        <AnimatePresence mode="wait">
          <motion.div key={selectedTech.id} initial={prefersReducedMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={prefersReducedMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.25
        }} className="mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch bg-card border border-border rounded-xl p-6 md:p-8 shadow-xs">
              {/* Left Column: Details & Capabilities */}
              <div className="lg:col-span-7 flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Badge variant="outline" className="border-border text-foreground bg-muted/40 font-normal">
                      {selectedTech.badge}
                    </Badge>
                    <span className="text-xs text-muted-foreground font-mono">
                      {selectedTech.specs}
                    </span>
                  </div>

                  <h3 className="text-xl sm:text-2xl font-semibold text-foreground tracking-tight mb-3">
                    {selectedTech.title}
                  </h3>

                  <p className="text-base text-muted-foreground leading-relaxed mb-6">
                    {selectedTech.description}
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-4 border-t border-border">
                    {/* Key Technical Highlights */}
                    <div>
                      <h4 className="text-xs uppercase tracking-wider font-semibold text-foreground/80 mb-3 flex items-center gap-1.5">
                        <Layers className="size-3.5 text-primary" />
                        Key Capabilities
                      </h4>
                      <ul className="space-y-2">
                        {selectedTech.features.map((feature, i) => <li key={i} data-index={i} className="flex items-start gap-2 text-sm text-foreground">
                            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                            <span>{feature}</span>
                          </li>)}
                      </ul>
                    </div>

                    {/* Common Use Cases */}
                    <div>
                      <h4 className="text-xs uppercase tracking-wider font-semibold text-foreground/80 mb-3 flex items-center gap-1.5">
                        <Activity className="size-3.5 text-primary" />
                        Applied Use Cases
                      </h4>
                      <ul className="space-y-2">
                        {selectedTech.useCases.map((useCase, i) => <li key={i} data-index={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <span className="h-1.5 w-1.5 rounded-full bg-border mt-2 shrink-0" />
                            <span>{useCase}</span>
                          </li>)}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Footer Action */}
                <div className="pt-6 mt-6 border-t border-border flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <ShieldCheck className="size-4 text-foreground/70" />
                    <span>Includes complete IQ/OQ validation protocols</span>
                  </div>
                  <Button asChild variant="default" size="sm">
                    <Link to={selectedTech.link}>
                      Explore Technology
                      <ArrowRight className="size-4 ml-1" />
                    </Link>
                  </Button>
                </div>
              </div>

              {/* Right Column: Visual Instrument Display */}
              <div className="lg:col-span-5 flex flex-col">
                <div className="relative w-full h-full min-h-[260px] md:min-h-[320px] rounded-lg overflow-hidden bg-muted/40 border border-border flex flex-col">
                  <img src={selectedTech.imageSrc} data-wvc-srcset={selectedTech.imageSrcset} data-wvc-sizes={selectedTech.imageSizes} alt={selectedTech.imageAlt} loading="lazy" decoding="async" className="w-full h-full object-cover object-center" />
                  <div className="absolute inset-x-0 bottom-0 bg-background/90 backdrop-blur-xs border-t border-border p-3 text-xs text-foreground flex items-center justify-between">
                    <span className="font-medium">{selectedTech.label}</span>
                    <span className="text-muted-foreground">Laboratory & In-line Ready</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Bottom Feature Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {capabilities.map((item, i) => {
          const IconComponent = item.icon;
          return <Card key={i} data-index={i} className="bg-card border-border shadow-xs hover:border-border/80 transition-colors">
                <CardHeader className="pb-2">
                  <div className="size-10 rounded-lg bg-secondary flex items-center justify-center text-secondary-foreground mb-3">
                    <IconComponent className="size-5 text-primary" />
                  </div>
                  <CardTitle className="text-lg font-semibold text-foreground">
                    {item.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {item.description}
                  </p>
                </CardContent>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}